import { useMemo } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useIsNFCSContractPaused } from 'modules/contracts/hooks/useIsNFCSContractPaused'
import { useNFCSState } from 'modules/nfcs/hooks/useNFCSState'
import { useNFCSFormMintActions } from './useNFCSFormMintActions'

import { NetworkGuardWrap } from 'shared/ui/common/NetworkGuardWrap'
import { NFCSFormPartHeader } from '../NFCSFormPartHeader'
import { NFCSFormPartFooter } from '../NFCSFormPartFooter'
import { NFCSFormPartSignButton } from '../NFCSFormPartSignButton'
import { NFCSFormPartAddressesWrap } from '../NFCSFormPartAddressesWrap'
import { NFCSFormPartAddress } from '../NFCSFormPartAddress'
import { NFCSFormPartSteps } from '../NFCSFormPartSteps'
import { NFCSFormMintDescription } from '../NFCSFormMintDescription'
import { NFCSFormPrimaryAddressSign } from '../NFCSFormPrimaryAddressSign'
import { NFCSFormStep } from 'modules/nfcs/types'

type Props = {
  onTxSubmit?: (txHash: string) => void
}

export function NFCSFormMint({ onTxSubmit }: Props) {
  const { walletAddress } = useWeb3()
  const {
    signedAddresses,
    nfcsStatus,
    currentFormStep,
    signs,
    handleCurrentFormStepChange,
  } = useNFCSState()
  const { isNFCSPaused, isLoading: isNFCSPausedLoading } =
    useIsNFCSContractPaused()

  const {
    mintTx,
    handleAddAddress,
    handleRemoveAddress,
    handleMintClick,
    isAddressPending,
  } = useNFCSFormMintActions({ onTxSubmit })

  const isLoading =
    isNFCSPausedLoading ||
    isAddressPending ||
    mintTx.isSigning ||
    nfcsStatus === 'loading'

  const isAddressAlreadyHasToken = nfcsStatus === 'generated'
  const isLastStep = currentFormStep === NFCSFormStep.Finish

  const isDisabledSubmit =
    isAddressAlreadyHasToken ||
    isNFCSPaused ||
    (isLastStep && !signs[signedAddresses[0]])

  const isCurrentAddressAdded = useMemo(() => {
    if (!walletAddress) return false
    return signedAddresses.includes(walletAddress)
  }, [signedAddresses, walletAddress])

  const isDisabledSign = Boolean(
    isAddressAlreadyHasToken || isNFCSPaused || isCurrentAddressAdded,
  )

  const subtitle = useMemo(() => {
    switch (currentFormStep) {
      case NFCSFormStep.Start:
        return 'Select primary address'
      case NFCSFormStep.Intermediate:
        return 'Add more addresses to the bundle'
      case NFCSFormStep.Finish:
        return 'Sign primary address'
      default:
        return ''
    }
  }, [currentFormStep])

  return (
    <>
      <NFCSFormPartSteps
        activeStepIndex={currentFormStep}
        stepsCount={3}
        onStepClick={handleCurrentFormStepChange}
      />
      <NFCSFormPartHeader title="Mint NFCS" subtitle={subtitle} />
      <NFCSFormMintDescription />
      <NetworkGuardWrap>
        <NFCSFormPartAddressesWrap>
          {signedAddresses.map((address, i) => (
            <NFCSFormPartAddress
              key={address}
              index={i}
              address={address}
              description={
                i === 0 && (
                  <NFCSFormPrimaryAddressSign
                    nfcsMode="mint"
                    isLoading={isAddressPending}
                    onClick={handleAddAddress}
                  />
                )
              }
              onRemove={
                i !== 0 && currentFormStep !== NFCSFormStep.Finish
                  ? handleRemoveAddress(address)
                  : undefined
              }
            />
          ))}
        </NFCSFormPartAddressesWrap>
        <NFCSFormPartSignButton
          isLoading={isLoading}
          isDisabled={isDisabledSign}
          isCurrentAddressAdded={isCurrentAddressAdded}
          showTaken={isAddressAlreadyHasToken}
          onAddAddress={handleAddAddress}
        />
      </NetworkGuardWrap>
      {currentFormStep !== NFCSFormStep.Start && (
        <NFCSFormPartFooter
          submitText={isLastStep ? 'Mint NFCS' : 'Continue'}
          estimatedGas={mintTx.estimatedGas}
          isLoading={isLoading}
          isContractPaused={isNFCSPaused}
          isDisabled={isDisabledSubmit}
          shouldShowHint={currentFormStep === NFCSFormStep.Finish}
          onSubmit={handleMintClick}
        />
      )}
    </>
  )
}
